import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";

/**
 * "Tell me when a sponsor is available" - shown only when a real search has
 * returned zero sponsors.
 *
 * HONESTY CONTRACT (read before changing any copy here)
 *  * Availability alerts are NOT running yet: Sponsor Me has no email provider
 *    configured. The form therefore says, on screen, that the request is saved
 *    and that we will email once alerts are switched on. It never says "the
 *    moment a sponsor joins", "instantly", or "we'll notify you right away".
 *  * Sending will be an admin-reviewed step, not an automatic trigger, so no
 *    immediacy is implied anywhere.
 *
 * PRIVACY CONTRACT
 *  * Two fields only: email and recovery pathway. No name, no account, no
 *    location, nothing about anyone's recovery history.
 *  * Consent is a separate, never pre-ticked checkbox, and the exact sentence
 *    is stored server-side with a timestamp.
 *  * The email address and the chosen pathway are NEVER passed to HeyCatch.
 *    The only analytics call here is a no-property event recording that
 *    somebody used the form at all.
 *  * The response carries a one-off management token, kept in component state
 *    only, so the person can remove the request immediately without an
 *    account. It is never written to storage or to a URL that gets logged.
 */

const PATHWAY_OPTIONS = [
  { value: "ANY", label: "Any recovery pathway" },
  { value: "AA", label: "AA - Alcoholics Anonymous" },
  { value: "NA", label: "NA - Narcotics Anonymous" },
  { value: "SMART", label: "SMART Recovery" },
  { value: "CMA", label: "CMA - Crystal Meth Anonymous" },
  { value: "OTHER", label: "Another pathway" },
];

const CONSENT_WORDING =
  "I agree to receive availability emails related to my selected recovery pathway.";

export default function AvailabilityAlertForm({ program = "ALL", className = "" }) {
  const initialPathway = PATHWAY_OPTIONS.some((p) => p.value === program) ? program : "ANY";
  const [email, setEmail] = useState("");
  const [pathway, setPathway] = useState(initialPathway);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(null); // { id, manage_token, notice }
  const [removed, setRemoved] = useState(false);
  const inFlight = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return; // blocks a mobile double-tap
    if (!consent || !email.trim()) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/availability-alerts", {
        email: email.trim(),
        pathway,
        consent: true,
      });
      setSaved({ ...data.alert, notice: data.message });
      // No properties at all: the email address and the pathway stay out of
      // third-party analytics by construction, not by filtering.
      trackOutcome(HEYCATCH_EVENTS.AVAILABILITY_ALERT_REQUESTED);
    } catch (err) {
      setError(apiError(err, "We couldn't save that just now. Please try again in a moment."));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!saved?.id || !saved?.manage_token) return;
    setBusy(true);
    try {
      await api.delete(`/availability-alerts/${saved.id}`, {
        params: { token: saved.manage_token },
      });
      setRemoved(true);
      setSaved(null);
      setEmail("");
      setConsent(false);
    } catch (err) {
      setError(apiError(err, "We couldn't remove that just now. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div
        data-testid="availability-alert-saved"
        className={`rounded-2xl border border-[#CFE4E4] bg-[#EAF3F4] p-5 text-left ${className}`}
      >
        <p className="flex items-center gap-2 font-display text-base font-bold text-[#134B52]">
          <Check className="w-5 h-5 shrink-0" aria-hidden="true" />
          Your request is saved
        </p>
        <p data-testid="availability-alert-notice" className="mt-2 text-sm text-[#2F5E63] leading-6">
          {saved.notice}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={remove}
          disabled={busy}
          data-testid="availability-alert-remove"
          className="mt-4 h-11 bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0] touch-manipulation"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Remove this request"}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="availability-alert-form"
      className={`rounded-2xl border border-[#E3DED4] bg-white p-5 text-left ${className}`}
    >
      <h3 className="flex items-center gap-2 font-display text-base font-bold text-[#1F2A2E]">
        <BellRing className="w-5 h-5 text-[#1F6F78] shrink-0" aria-hidden="true" />
        Get notified when a sponsor becomes available
      </h3>
      <p className="mt-2 text-sm text-[#4B5A60] leading-6">
        Tell us which recovery pathway you are looking for and we will let you know when an
        available sponsor is added.
      </p>
      <p
        data-testid="availability-alert-pending-note"
        className="mt-2 text-sm text-[#6B4A22] bg-[#FDF6E7] border border-[#E6D3A3] rounded-xl px-3 py-2 leading-6"
      >
        Availability alerts are not switched on yet. Your request is saved now, and we will email
        you once we are able to send these alerts.
      </p>

      {removed ? (
        <p data-testid="availability-alert-removed" className="mt-3 text-sm text-[#1F6F78]">
          That request has been deleted.
        </p>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="availability-alert-email"
            className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]"
          >
            Email
          </label>
          <Input
            id="availability-alert-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="availability-alert-email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@example.com"
            className="mt-1.5 h-12 text-base bg-white border-[#E3DED4]"
          />
        </div>
        <div>
          <label
            htmlFor="availability-alert-pathway"
            className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]"
          >
            Recovery pathway
          </label>
          <Select value={pathway} onValueChange={setPathway}>
            <SelectTrigger
              id="availability-alert-pathway"
              data-testid="availability-alert-pathway"
              className="mt-1.5 h-12 bg-white border-[#E3DED4] focus:ring-[#1F6F78]"
            >
              <SelectValue placeholder="Choose a pathway" />
            </SelectTrigger>
            <SelectContent>
              {PATHWAY_OPTIONS.map((p) => (
                <SelectItem
                  key={p.value}
                  value={p.value}
                  data-testid={`availability-alert-pathway-${p.value}`}
                >
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <label className="mt-4 flex items-start gap-3 rounded-xl border border-[#E3DED4] bg-[#FBF9F5] p-4">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(Boolean(v))}
          data-testid="availability-alert-consent"
          className="mt-0.5"
        />
        <span className="text-sm text-[#1F2A2E] leading-6">
          {CONSENT_WORDING}
          <span className="block text-xs text-[#7A868B] mt-1">
            Used only for availability emails about that pathway - never for marketing, never shown
            publicly, and you can remove it at any time.{" "}
            <Link
              to="/privacy"
              data-testid="availability-alert-privacy-link"
              className="font-semibold text-[#1F6F78] hover:underline"
            >
              Read our Privacy Policy
            </Link>
          </span>
        </span>
      </label>

      {error ? (
        <p
          data-testid="availability-alert-error"
          role="alert"
          className="mt-3 text-sm text-[#B42318] bg-[#FBE6E4] rounded-lg px-3 py-2"
        >
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={busy || !consent || !email.trim()}
        aria-busy={busy}
        data-testid="availability-alert-submit"
        className="mt-4 w-full sm:w-auto h-12 px-6 bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] disabled:opacity-60 text-white touch-manipulation"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
            Saving...
          </>
        ) : (
          "Save my request"
        )}
      </Button>
    </form>
  );
}
